/**
 * Google Analytics 4 helpers.
 * gtag.js is loaded in index.html; these helpers wrap window.gtag safely.
 */
import { trackMetaEvent } from "@/lib/meta-pixel";

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
    dataLayer?: unknown[];
  }
}

const GA_MEASUREMENT_ID = import.meta.env["VITE_LOVABLE_CONNECTOR_GOOGLE_ANALYTICS_API_KEY"] ?? "G-4959PFJEQZ";

export function trackPageview(path: string, title?: string) {
  if (typeof window === "undefined" || !window.gtag) return;
  window.gtag("config", GA_MEASUREMENT_ID, {
    page_path: path,
    page_title: title ?? document.title,
  });
}

export function trackEvent(
  name: string,
  params?: Record<string, unknown>,
) {
  if (typeof window === "undefined" || !window.gtag) return;
  window.gtag("event", name, params ?? {});
}

/**
 * Fires a conversion at most once per browser session for a given key.
 * Guards against refresh / back-navigation double counting (notably on the
 * order success page, which can be reloaded with the same session id).
 */
export function fireOnce(key: string, fn: () => void): boolean {
  if (typeof window === "undefined") return false;
  try {
    const storageKey = `mfi_conv:${key}`;
    if (window.sessionStorage.getItem(storageKey)) return false;
    window.sessionStorage.setItem(storageKey, "1");
  } catch {
    // Private mode / storage disabled — fall through and fire once per load.
  }
  fn();
  return true;
}

/**
 * Paid-order conversion. Call only after the order is confirmed paid.
 * Never pass name, email, phone or address — no PII to GA4/Meta.
 */
export function trackPurchase(params: {
  orderId: string;
  valueUsd: number;
  fenceType?: string | null;
  linearFeet?: number | null;
  fulfillmentType?: string | null;
}): boolean {
  return fireOnce(`purchase:${params.orderId}`, () => {
    trackEvent("purchase", {
      transaction_id: params.orderId,
      value: Number(params.valueUsd.toFixed(2)),
      currency: "USD",
      items: [
        {
          item_id: params.fenceType ?? "fence",
          item_name: `${params.fenceType ?? "fence"} fence`,
          item_category: params.fulfillmentType ?? "installation",
          quantity: 1,
          price: Number(params.valueUsd.toFixed(2)),
        },
      ],
      linear_feet: params.linearFeet ?? undefined,
    });
    trackMetaEvent(
      "Purchase",
      {
        value: Number(params.valueUsd.toFixed(2)),
        currency: "USD",
        content_type: "product",
        content_ids: [params.fenceType ?? "fence"],
      },
      { eventID: `purchase-${params.orderId}` },
    );
  });
}

/** Lead conversion — GA4 generate_lead + Meta Lead, deduplicated. */
export function trackLeadConversion(params: {
  method: string;
  source?: string | undefined;
  leadKind?: string | undefined;
  /** Id of the accepted lead row. Preferred dedupe scope: one Lead per saved record. */
  leadId?: string | undefined;
  dedupeKey?: string | undefined;
}): boolean {
  const key =
    params.dedupeKey ??
    (params.leadId
      ? `lead:${params.method}:${params.leadId}`
      : `lead:${params.method}:${params.source ?? ""}`);
  return fireOnce(key, () => {
    trackEvent("generate_lead", {
      method: params.method,
      source: params.source,
      lead_kind: params.leadKind,
    });
    // Non-PII metadata only — never name, email, phone or address.
    trackMetaEvent(
      "Lead",
      { content_name: params.source ?? params.method, content_category: params.method },
      params.leadId ? { eventID: `lead-${params.leadId}` } : undefined,
    );
  });
}

/** High-value conversion events — wire to forms, calls, bookings */
export const conversions = {
  quickQuoteSubmit: (source?: string, leadId?: string) =>
    trackLeadConversion({ method: "quick_quote", source, leadId }),
  fullBookingSubmit: () =>
    trackLeadConversion({ method: "full_booking" }),
  contactFormSubmit: (leadId?: string) =>
    trackLeadConversion({ method: "contact_form", leadId }),
  estimatorSubmit: (source?: string, leadId?: string) =>
    trackLeadConversion({ method: "estimator", source, leadId }),
  beatMyEstimateSubmit: (leadId?: string) =>
    trackLeadConversion({ method: "beat_my_estimate", leadId }),
  serviceHeroQuoteSubmit: (service?: string) =>
    trackLeadConversion({ method: "service_hero_quote", source: `service-hero:${service ?? "unknown"}` }),
  franchiseApplicationSubmit: () =>
    trackLeadConversion({ method: "franchise_application" }),
  referralInviteSubmit: () =>
    trackLeadConversion({ method: "referral_invite" }),
  mediaSubmission: () =>
    trackLeadConversion({ method: "media_submission" }),
  leadSubmit: (params: { source?: string; lead_kind?: string; method?: string }) =>
    trackLeadConversion({
      method: params.method ?? "lead",
      source: params.source,
      leadKind: params.lead_kind,
    }),
  /** Fired once on the /thanks confirmation page. Useful as a conversion endpoint
   * distinct from the initial form submit (filters out double-submits and bots). */
  leadConfirmed: (params: { source?: string; lead_kind?: string }) =>
    trackEvent("lead_confirmed", params),
  phoneClick: (location?: string) =>
    trackEvent("phone_call_click", { location }),
  formFieldFocus: (form: string, field: string) =>
    trackEvent("form_field_focus", { form, field }),
  formAbandon: (form: string, lastField?: string) =>
    trackEvent("form_abandon", { form, last_field: lastField }),
  resumeBannerShown: (source?: string) =>
    trackEvent("quote_resume_banner_shown", { source }),
  resumeBannerClick: (source?: string) =>
    trackEvent("quote_resume_banner_click", { source }),
  proofMediaOpen: (placement: string, mediaType: string) =>
    trackEvent("proof_media_open", { placement, media_type: mediaType }),
  publicProjectOpen: (placement: string, fenceType?: string | null) =>
    trackEvent("public_project_open", { placement, fence_type: fenceType ?? "unspecified" }),
  proofCtaClick: (placement: string) =>
    trackEvent("proof_cta_click", { placement }),
};

